import React, { useState } from "react";
import {
    Avatar,
    Button,
    Modal,
    Typography,
    Progress,
    message as antdMessage,
    Row,
    Col,
    Tooltip,
    Tag,
    Divider,
} from "antd";
import {
    TrophyOutlined,           
    UsergroupAddOutlined,     
    LineChartOutlined,        
    AimOutlined,              
    ThunderboltOutlined,      
    LockOutlined,
} from "@ant-design/icons";
import { useSelector } from "react-redux";
import MemberServiceInstance from "../../../services/MemberService";
import { getErrorMessage } from "../../../helpers/ApiResponseHelper";
import dayjs from "dayjs";

const { Title, Text } = Typography;

const MARKET_ICON = (code) => {
    switch ((code || "").toUpperCase()) {
        case "1X2": return <TrophyOutlined />;
        case "BTTS": return <UsergroupAddOutlined />;
        case "OU": return <LineChartOutlined />;
        case "AH": return <AimOutlined />;
        case "INPLAY": return <ThunderboltOutlined />;
        default: return <LineChartOutlined />;
    }
};

function statusTag(s) {
    return <Tag className="m-0" color={s?.color}>{s?.name}</Tag>;
}

function PremiumPredictionModal({
    open,
    onClose,
    prediction,
    fixture = {},
    cta = null,
}) {
    const sys = prediction?.system;
    const mem = prediction?.member;

    const kickoff = fixture?.dateISO ? dayjs(fixture.dateISO).format("ddd, DD MMM YYYY · HH:mm") : null;

    const options = sys?.options || [];
    const optionValues = options?.map((item) => item?.value);
    const probs = sys?.prob || {};
    const best = sys?.bestValue || "-";
    const expectedOdds = mem?.expectedOdds;
    const stakeNum = Number(mem?.stake ?? 0);
    const oddsNum = expectedOdds != null ? Number(expectedOdds) : NaN;
    const expectedWinNum = mem?.expectedWin != null
        ? Number(mem?.expectedWin)
        : (isNaN(oddsNum) ? NaN : stakeNum * oddsNum);

    const fmt2 = (n) => (isNaN(n) ? "—" : n.toFixed(2));
    const confPct = sys ? (sys.confidence * 100).toFixed(1) : null;

    const labelOf = (v) => options.find(o => o.value === v)?.label || v;
    const descOf = (v) => options.find(o => o.value === v)?.description || "";

    const strokeOf = (v) => {
        switch ((v || "").toUpperCase()) {
            // 1X2
            case "1":  
                return { from: "#52c41a", to: "#95de64" };   
            case "X":   
                return { from: "#1677ff", to: "#69b1ff" };  
            case "2":  
                return { from: "#faad14", to: "#ffd666" };  

            // BTTS
            case "YES":
                return { from: "#722ed1", to: "#9254de" };  
            case "NO":
                return { from: "#eb2f96", to: "#ff85c0" };   

            // O/U 2.5
            case "OVER":
                return { from: "#13c2c2", to: "#36cfc9" }; 
            case "UNDER":
                return { from: "#a0d911", to: "#bae637" };  

            // Default
            default:
                return { from: "#d9d9d9", to: "#bfbfbf" }; 
        }
    };

    return (
        <Modal
            centered
            open={open}
            closeIcon={null}
            onCancel={onClose}
            style={{ minWidth: '55%' }}
            title={null}
            footer={cta}
            destroyOnHidden
        >
            <div className="pxp-hero" style={{ marginTop: 10 }}>
                <div className="pxp-hero-inner">
                    <div className="team">
                        <Avatar src={fixture?.home?.logo} size={70} />
                    </div>

                    <div className="vs">
                        <Text type="secondary" className="lab">Fixture #{prediction?.fixtureId}</Text>
                        <Title level={3} style={{ margin: 0, color: "#0f172a" }}>
                            {fixture?.home?.name || "Home"} <span className="muted">vs</span> {fixture?.away?.name || "Away"}
                        </Title>
                        {kickoff && <Text type="secondary">{kickoff}</Text>}
                    </div>

                    <div className="team team--away">
                        <Avatar src={fixture?.away?.logo} size={70} />
                    </div>
                </div>
            </div>

            <Row gutter={[16, 16]} className="pxp-summary">
                <Col xs={24} md={12}>
                    <div className="pxp-card">
                        <Text type="secondary" strong>Predicted outcome</Text>
                        <Title level={4} style={{ margin: "4px 0 0" }}>
                            {labelOf(best)}
                        </Title>
                        <Text type="secondary">{descOf(best)}</Text>
                    </div>
                </Col>
                <Col xs={12} md={6}>
                    <div className="pxp-card centered">
                        <Text type="secondary" strong>Accuracy</Text>
                        <Title level={4} style={{ margin: "4px 0 0" }}>
                            {confPct ? `${confPct}%` : "—"}
                        </Title>
                    </div>
                </Col>
                <Col xs={12} md={6}>
                    <div className="pxp-card centered">
                        <Text type="secondary" strong>Status</Text>
                        <div style={{ marginTop: 6 }}>{statusTag(mem?.status)}</div>
                    </div>
                </Col>
            </Row>
            <Row gutter={[16, 16]} className="pxp-summary">
                <Col xs={24} md={8}>
                    <div className="pxp-card centered">
                        <Text type="secondary" strong>Stake</Text>
                        <Title level={4} style={{ margin: "4px 0 0" }}>
                            {fmt2(stakeNum)}
                        </Title>
                        <Text type="secondary" style={{ fontSize: 12 }}>units</Text>
                    </div>
                </Col>

                <Col xs={12} md={8}>
                    <div className="pxp-card centered">
                        <Text type="secondary" strong>Odds (decimal)</Text>
                        <Title level={4} style={{ margin: "4px 0 0" }}>
                            {fmt2(oddsNum)}
                        </Title>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                            for {labelOf(best)}
                        </Text>
                    </div>
                </Col>

                <Col xs={12} md={8}>
                    <div className="pxp-card centered">
                        <Text type="secondary" strong>Potential Return</Text>
                        <Title level={4} style={{ margin: "4px 0 0" }}>
                            {fmt2(expectedWinNum)}
                        </Title>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                            Includes stake if settled as a win
                        </Text>
                    </div>
                </Col>
            </Row>

            <div className="pxp-block" style={{ maxHeight: '300px', overflowY: 'scroll' }}>
                <Text type="secondary" strong>Probability breakdown</Text>
                <div style={{ marginTop: 10 }}>
                    {optionValues?.filter(k => probs[k] !== undefined).map((k) => {
                        const pct = Number((probs[k] * 100).toFixed(1));
                        return (
                            <div key={k} className="pxp-bar">
                                <div className="pxp-bar-head">
                                    <Text strong>{labelOf(k)} ({pct}%)</Text>
                                </div>
                                <Progress
                                    percent={pct}
                                    status="active"
                                    strokeColor={strokeOf(k)}
                                />
                                {descOf(k) && <Text type="secondary" style={{ fontSize: 12 }}>{descOf(k)}</Text>}
                            </div>
                        );
                    })}
                </div>
            </div>

            <style>{`
        .pxp-hero {
          background: radial-gradient(1200px 300px at 50% -10%, #eef7ff 0%, #ffffff 60%),
                      linear-gradient(0deg, #fafafa, #ffffff);
          border: 1px solid rgba(2,6,23,.06);
          border-radius: 12px;
          padding: 14px;
          margin: -8px 0 10px;
        }
        .pxp-hero-inner {
          display: grid;
          grid-template-columns: 1fr auto 1fr;
          align-items: center;
          gap: 10px;
          text-align: center;
        }
        .pxp-hero .team {
          display: flex; align-items: center; gap: 8px; justify-content: flex-start;
        }
        .pxp-hero .team--away { justify-content: flex-end; }
        .pxp-hero .team-name { font-weight: 800; color:#0f172a }
        .pxp-hero .vs .lab { font-size: 12px; display:block; }
        .pxp-hero .muted { color:#64748b; font-weight:700; }

        .pxp-summary { margin: 10px 0 6px; }
        .pxp-card {
          border: 1px solid rgba(2,6,23,.06);
          background: #fff;
          border-radius: 10px;
          padding: 10px 12px;
        }
        .pxp-card.centered { text-align:center; }

        .pxp-block { margin-top: 8px; }
        .pxp-bar { margin-bottom: 12px; }
        .pxp-bar-head {
          display:flex; align-items:center; justify-content:space-between;
          margin-bottom: 4px;
        }
      `}</style>
        </Modal>
    );
}


export default function PredictOptions({ fixtureId, fixture }) {
    const token = useSelector((s) => s.Auth.token);
    const subscriptionType = useSelector((s) => s.Member.subscriptionType);
    const markets = useSelector((s) => s.Utility.markets);

    const [loadingCode, setLoadingCode] = useState(null);
    const [prediction, setPrediction] = useState(null);
    const [visible, setVisible] = useState(false);

    const isLocked = (code) => {
        const subIsFree = String(subscriptionType || '').trim().replace(/^['"]+|['"]+$/g, '').toLowerCase() === 'free';
        const not1x2 = String(code || '').trim().toUpperCase() !== '1X2';
        const istHtFt = String(code || '').trim().toUpperCase() === 'HT_FT';
        return subIsFree && not1x2 && istHtFt;
    };

    const handlePredict = async (marketCode) => {
        if (isLocked(marketCode)) return;
        try {
            setLoadingCode(marketCode);
            const res = await MemberServiceInstance.predictMarket(token, { marketCode, fixtureId });
            const { success, result, message } = res.data || {};
            if (success) {
                setPrediction(result);
                setVisible(true);
            } else {
                await antdMessage.error(message || "Prediction failed");
            }
        } catch (err) {
            await antdMessage.error(getErrorMessage(err?.response, err?.message));
        } finally {
            setLoadingCode(null);
        }
    };

    return (
        <>
            <Row gutter={[16, 16]} style={{ marginTop: 24 }}>
                <Divider className="w-100" style={{ marginTop: 0, marginBottom: 10, borderColor: '#2d5fc3' }} />
                {markets?.map((m) => {
                    const code = m?.code;
                    const locked = isLocked(code);
                    const loading = loadingCode === code;

                    const Btn = (
                        <Button
                            block
                            size="large"
                            shape="round"
                            type={locked ? "default" : "primary"}
                            icon={locked ? <LockOutlined /> : MARKET_ICON(code)}
                            loading={loading}
                            disabled={locked}
                            onClick={() => handlePredict(code)}
                            className={`px-market-btn ${locked ? "is-locked" : "is-live"}`}
                        >
                            <div className="px-market-text">
                                <span className="px-market-title">
                                    {code === "1X2" ? "Predict 1X2" : m?.name || code}
                                </span>
                                <span className="px-market-sub">
                                    {locked ? "Upgrade to unlock" : "AI prediction"}
                                </span>
                            </div>
                        </Button>
                    );

                    return (
                        <Col xl={6} lg={8} md={12} sm={12} xs={12} key={code}>
                            {locked ? (
                                <Tooltip title="This market is available on Pro/Elite plans">
                                    {Btn}
                                </Tooltip>
                            ) : (
                                Btn
                            )}
                        </Col>
                    );
                })}
            </Row>

            <PremiumPredictionModal
                open={visible}
                onClose={() => setVisible(false)}
                prediction={prediction}
                fixture={{
                    dateISO: fixture?.fixture?.date,
                    home: { name: fixture?.teams?.home?.name, logo: fixture?.teams?.home?.logo },
                    away: { name: fixture?.teams?.away?.name, logo: fixture?.teams?.away?.logo },
                }}
                cta={[
                    <Button key="mypred" type="primary" href="/my-predictions">Go to My Predictions</Button>,
                    <Button key="close" onClick={() => setVisible(false)}>Close</Button>,
                ]}
            />

            <style>{`
        .px-market-btn {
  /* layout */
  display: flex;
  flex-direction: column;     /* icon on top, text below */
  align-items: center;
  justify-content: center;
  gap: 6px;

  /* sizing */
  width: 100%;
  min-height: 64px;           /* baseline height */
  height: auto;               /* allow content to grow */
  padding: 10px 12px;
  line-height: 1.2;

  /* icon */
}
.px-market-btn .anticon {
  font-size: 20px;
  margin: 0;                  /* no side margin in column layout */
}

/* text wrapper */
.px-market-text {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;

  /* prevent overflow */
  max-width: 100%;
}

/* primary line */
.px-market-title {
  font-weight: 700;
  font-size: 14px;
  white-space: normal;        /* allow wrap */
  word-break: break-word;     /* break long words */
  overflow: hidden;           /* clamp below */
  display: -webkit-box;
  -webkit-line-clamp: 2;      /* up to 2 lines */
  -webkit-box-orient: vertical;
}

/* secondary line */
.px-market-sub {
  font-size: 12px;
  color: #f5f7fb;
  white-space: normal;
  word-break: break-word;
  opacity: 0.95;
}

/* live (unlocked) look */
.px-market-btn.is-live {
  background: linear-gradient(180deg, #1677ff, #165dff);
  box-shadow: 0 8px 16px rgba(22,119,255,.25);
}
.px-market-btn.is-live:hover { filter: brightness(1.05); }

/* disabled look */
.px-market-btn.is-locked {
  border: 1px dashed rgba(0,0,0,.12)!important;
  background: #f5f7fb!important;
  color: rgba(0,0,0,.45)!important;
}
.px-market-btn.is-locked .px-market-title { color: rgba(0,0,0,.45); }
.px-market-btn.is-locked .px-market-sub   { color: rgba(0,0,0,.35); }

/* tighter on small phones */
@media (max-width: 5960px) {
  .px-market-title { font-size: 13px; -webkit-line-clamp: 2; }
  .px-market-sub   { font-size: 11px; }
  .px-market-btn .anticon { display: none; }
  .ant-btn-icon { display: none; }
}
      `}</style>
        </>
    );
}
